import React, { useCallback, useEffect, useState } from 'react';
import { Drawer, Space, Typography, Button, Upload, Spin, Input } from 'antd';
import type { RcFile } from 'antd/es/upload';
import type { UploadFile } from 'antd/es/upload/interface';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import AttributeService from '../../services/AttributeService';
import DocumentService from '../../services/DocumentService';
import { openNotification } from '../../utils';

interface AttributePreparationDrawerProps {
  attributeId: number | null;
}

export const ATTRIBUTE_PREPARATION_UPDATED_EVENT = 'attribute-preparation-updated';

const normalizePreparationText = (value: string): string | null => {
  if (!value) {
    return null;
  }
  const parsedHtml = new DOMParser().parseFromString(value, 'text/html');
  const plainText = (parsedHtml.body.textContent ?? '')
    .replace(/\u00a0/g, ' ')
    .trim();
  return plainText ? value : null;
};

const AttributePreparationDrawer: React.FC<AttributePreparationDrawerProps> = ({ attributeId }) => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attributeName, setAttributeName] = useState<string>('');
  const [editorValue, setEditorValue] = useState<string>('');
  const [titleValue, setTitleValue] = useState<string>('');
  const [payload, setPayload] = useState<any | null>(null);
  const [selectedFile, setSelectedFile] = useState<RcFile | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [documentId, setDocumentId] = useState<number | null>(null);
  const [existingDocument, setExistingDocument] = useState<any | null>(null);

  const resetState = useCallback(() => {
    setVisible(false);
    setLoading(false);
    setSaving(false);
    setAttributeName('');
    setEditorValue('');
    setTitleValue('');
    setPayload(null);
    setSelectedFile(null);
    setFileList([]);

    setDocumentId(null);
    setExistingDocument(null);
  }, []);

  const buildUpdatePayload = useCallback((attribute: any) => {
    if (!attribute) {
      return null;
    }
    return {
      id: attribute.id,
      name: attribute.name,
      description: attribute.description,
      price: attribute.price,
      offeringId: attribute.offeringId ?? attribute.offering?.id ?? null,
      toolId: attribute.toolId ?? attribute.tool?.id ?? null,
      groupId: attribute.groupId ?? attribute.group?.id ?? null,
      fieldControlTime: attribute.fieldControlTime,
      reportPreparationTime: attribute.reportPreparationTime,
      unit: attribute.unit,
      period: attribute.period,
      order: attribute.order ?? attribute.orderNo ?? null,
      isActive: attribute.isActive,
      isDefault: attribute.isDefault,
      countable: attribute.countable,
      preparationText: attribute.preparationText ?? '',
      preparationTitle: attribute.preparationTitle ?? '',
      preparationDocumentId:
        attribute.preparationDocumentId ?? attribute.preparationDocument?.id ?? null,
    };
  }, []);

  const fetchAttributeDetails = useCallback(
    async (id: number) => {
      setLoading(true);
      setSelectedFile(null);
      setFileList([]);
      try {
        const response = await AttributeService.get(id);
        const detail = response.data;
        const preparedPayload = buildUpdatePayload(detail);
        if (!preparedPayload) {
          throw new Error('Parametre bilgisi alınamadı');
        }
        setPayload(preparedPayload);
        setEditorValue(preparedPayload.preparationText ?? '');
        setTitleValue(preparedPayload.preparationTitle ?? '');
        setDocumentId(preparedPayload.preparationDocumentId ?? null);
        setAttributeName(detail?.name ?? '');

        if (preparedPayload.preparationDocumentId) {
          try {
            const documentResponse: any = await DocumentService.getDocument(preparedPayload.preparationDocumentId);
            setExistingDocument(documentResponse?.data || null);
          } catch (docError) {
            console.error('Error fetching preparation document:', docError);
            setExistingDocument(null);
          }
        } else {
          setExistingDocument(null);
        }
      } catch (error) {
        console.error('Error fetching attribute preparation info:', error);
        openNotification('error', 'Hata', 'Ön hazırlık bilgileri alınamadı.');
        resetState();
      } finally {
        setLoading(false);
      }
    },
    [buildUpdatePayload, resetState]
  );

  useEffect(() => {
    if (!attributeId) {
      resetState();
      return;
    }
    setVisible(true);
    fetchAttributeDetails(attributeId);
  }, [attributeId, fetchAttributeDetails, resetState]);

  const handleBeforeUpload = (file: RcFile) => {
    const isPdf =
      file.type === 'application/pdf' ||
      file.name?.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      openNotification('error', 'Hata', 'Lütfen PDF formatında bir dosya seçiniz.');
      return Upload.LIST_IGNORE;
    }
    const uploadItem: UploadFile = {
      uid: `${Date.now()}`,
      name: file.name,
      status: 'done',
      originFileObj: file,
    };
    setSelectedFile(file);
    setFileList([uploadItem]);
    setDocumentId(null);
    setExistingDocument(null);
    return false;
  };

  const handleRemoveSelectedFile = () => {
    setSelectedFile(null);
    setFileList([]);
    return true;
  };

  const handleRemoveExistingDocument = () => {
    setDocumentId(null);
    setExistingDocument(null);
    setSelectedFile(null);
    setFileList([]);
  };

  const handleClose = () => {
    setVisible(false);
  };

  const handleSave = async () => {
    if (!payload || !payload.id) {
      return;
    }
    setSaving(true);
    try {
      let preparedDocumentId = documentId;
      if (selectedFile) {
        const uploadResponse: any = await DocumentService.postDocument(selectedFile as any, selectedFile.name);
        preparedDocumentId =
          uploadResponse?.data?.id ??
          uploadResponse?.data?.data?.id ??
          null;
        if (!preparedDocumentId) {
          throw new Error('Belge yüklenemedi');
        }
      }

      const updatePayload = {
        ...payload,
        preparationText: normalizePreparationText(editorValue),
        preparationTitle: titleValue.trim(),
        preparationDocumentId: preparedDocumentId,
      };

      await AttributeService.update(updatePayload.id, updatePayload);
      openNotification('success', 'Başarılı', 'Ön hazırlık bilgileri güncellendi.');
      window.dispatchEvent(new CustomEvent(ATTRIBUTE_PREPARATION_UPDATED_EVENT, { detail: { attributeId: updatePayload.id } }));
      setVisible(false);
    } catch (error) {
      console.error('Error updating preparation info:', error);
      openNotification('error', 'Hata', 'Ön hazırlık bilgileri güncellenemedi.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={visible && Boolean(attributeId)}
      onClose={handleClose}
      destroyOnClose
      width={720}
      title={
        attributeName
          ? `${attributeName} Ön Hazırlık Metni`
          : 'Ön Hazırlık Metni'
      }
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 48 }}>
          <Spin size="large" />
        </div>
      ) : (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Typography.Text strong>Başlık</Typography.Text>
            <Input
              value={titleValue}
              onChange={e => setTitleValue(e.target.value)}
              maxLength={255}
              placeholder="Başlık giriniz"
              style={{ marginTop: 12 }}
            />
          </div>
          <div>
            <Typography.Text strong>Ön Hazırlık Metni</Typography.Text>
            <div style={{ marginTop: 12 }}>
              <ReactQuill
                theme="snow"
                value={editorValue}
                onChange={setEditorValue}
                style={{ minHeight: 200 }}
              />
            </div>
          </div>
          <div>
            <Typography.Text strong>Ön Hazırlık PDF</Typography.Text>
            <Space direction="vertical" style={{ width: '100%', marginTop: 12 }}>
              {existingDocument ? (
                <Space direction="vertical" size={4}>
                  <Typography.Text>
                    {existingDocument?.code ?? 'Mevcut dosya'}
                  </Typography.Text>
                  {existingDocument?.preSignUrl ? (
                    <Button
                      type="link"
                      href={existingDocument.preSignUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Dosyayı İndir
                    </Button>
                  ) : null}
                  <Button type="link" danger onClick={handleRemoveExistingDocument}>
                    Mevcut Dosyayı Kaldır
                  </Button>
                </Space>
              ) : null}
              <Upload
                accept=".pdf"
                beforeUpload={handleBeforeUpload}
                onRemove={handleRemoveSelectedFile}
                fileList={fileList}
                maxCount={1}
              >
                <Button>PDF Seç</Button>
              </Upload>
              {selectedFile ? (
                <Typography.Text type="secondary">
                  Seçilen dosya: {selectedFile.name}
                </Typography.Text>
              ) : null}
              {!existingDocument && !selectedFile ? (
                <Typography.Text type="secondary">
                  PDF yüklemediğinizde parametreye ait doküman kaydedilmez.
                </Typography.Text>
              ) : null}
            </Space>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={handleClose}>İptal</Button>
            <Button
              type="primary"
              loading={saving}
              onClick={handleSave}
              disabled={loading}
              className="d-button dark-button width-auto"
            >
              Kaydet
            </Button>
          </div>
        </Space>
      )}
    </Drawer>
  );
};

export default AttributePreparationDrawer;
